/**
 * Procurement Demo — the suggested questions on Nami's empty state.
 *
 * THROWAWAY DEMO CODE. Each procurement page offers a few prompts that fit it.
 * Every one is answered by the scripted interceptor: either one of the prompts
 * in `script.ts` (which also opens the page the answer points at), an overview
 * answer in `overview-answers.ts`, an answer that belongs to the page in
 * `page-answers.ts` (contracts list, bid page, supplier, material), a
 * question or job on one agreement's page in `pages/contracts/contract-chat.ts`,
 * a replenishment job in `replenishment-chat.ts`, a forecast change in
 * `forecast-override.ts`, or the explanation of a forecast in
 * `pages/forecast/forecast-explain.ts`. `suggestions.test.ts` checks that each
 * chip lands on one of them.
 */

import { getSupplier } from '../data/suppliers';
import {
  contractChips,
  contractIdFromPath,
} from '../pages/contracts/contract-chat';
import { ASK_SUGGESTIONS } from '../pages/overview/ask-content';
import { EXPLAIN_FORECAST_PROMPT } from '../pages/forecast/forecast-explain';
import { pageAnswersForPath } from './page-answers';
import { replenishmentSuggestions } from './replenishment-chat';

const [
  CHEAPEST_COPPER_WIRE,
  AGREEMENT_DECISIONS,
  GENTLY_SERVICE_LEVELS_OVERVIEW,
  CORVANE_SPEND_AND_REBATE,
] = ASK_SUGGESTIONS.map((suggestion) => suggestion.question);

const INVOICE_HOLDS = 'Which invoices are on hold for contract violations?';
const VITREX_HOLD = `What is at risk from the ${getSupplier('sup-vitrex').shortName} breaker hold?`;
const DRIVE_FORECAST = 'Show me the forecast for drives at the panel shop';
const GENTLY_SERVICE_LEVELS = 'What service levels does Gently AI commit to?';
const SELL_MORE = "We're going to sell 30% more over the next 6 months";

const SUGGESTIONS_BY_PREFIX: { prefix: string; suggestions: string[] }[] = [
  {
    prefix: '/procurement/overview',
    suggestions: [
      CHEAPEST_COPPER_WIRE,
      AGREEMENT_DECISIONS,
      GENTLY_SERVICE_LEVELS_OVERVIEW,
      CORVANE_SPEND_AND_REBATE,
    ],
  },
  {
    prefix: '/procurement/invoice-compliance',
    suggestions: [INVOICE_HOLDS, CORVANE_SPEND_AND_REBATE],
  },
  {
    prefix: '/procurement/forecast/bom',
    suggestions: [VITREX_HOLD, SELL_MORE],
  },
  {
    prefix: '/procurement/forecast/details',
    suggestions: [
      EXPLAIN_FORECAST_PROMPT,
      SELL_MORE,
      DRIVE_FORECAST,
      VITREX_HOLD,
    ],
  },
  {
    prefix: '/procurement/forecast',
    suggestions: [DRIVE_FORECAST, VITREX_HOLD],
  },
  {
    prefix: '/procurement/contracts',
    suggestions: [
      GENTLY_SERVICE_LEVELS,
      AGREEMENT_DECISIONS,
      CORVANE_SPEND_AND_REBATE,
    ],
  },
];

/** The contracts list: its own questions about every agreement, then two broader ones. */
function contractsListSuggestions(): string[] {
  return [
    ...pageAnswersForPath('/procurement/contracts').map(
      (answer) => answer.question,
    ),
    GENTLY_SERVICE_LEVELS,
    AGREEMENT_DECISIONS,
  ];
}

const REPLENISHMENT_PAGE = '/procurement/replenishment/board';

/** The prompts to offer on this page, or undefined when it has none. */
export function suggestionsForPath(pathname: string): string[] | undefined {
  if (pathname === '/procurement/contracts') return contractsListSuggestions();
  // An agreement's page: its own questions and jobs, a few at a time.
  const agreementId = contractIdFromPath(pathname);
  if (agreementId) return contractChips(agreementId);
  const replenishment = replenishmentSuggestions(pathname);
  if (replenishment) {
    // The page covers the panel shop too, so its hold question belongs here.
    return pathname === REPLENISHMENT_PAGE
      ? [...replenishment, VITREX_HOLD]
      : replenishment;
  }
  const pageAnswers = pageAnswersForPath(pathname);
  if (pageAnswers.length > 0) {
    return pageAnswers.map((answer) => answer.question);
  }
  return SUGGESTIONS_BY_PREFIX.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )?.suggestions;
}
